"use client";

import * as yup from "yup";
import { TextField, Button } from "@mui/material";
import { useState } from "react";
import { getMessage, getYupErrorMessages } from "@/lib/error";
import useToast from "@/stores/toast";
import config from "@/app.config";
import axios from "axios";
import Image from "next/image";

const schema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
    .required("Phone is required"),
  message: yup.string().required("Message is required"),
});

export default function Contact() {
  const INITIAL_VALUE = {
    name: "",
    email: "",
    phone: "",
    message: "",
  };
  const { toast } = useToast();
  const [state, setState] = useState(INITIAL_VALUE);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submitHandler = async () => {
    try {
      setLoading(true);
      setErrors({});

      await schema.validate(state, { abortEarly: false });

      await axios.post(`${config.app.apiBaseUrl}/api/v2/contact`, state, {
        headers: {
          Accept: "application/json",
        },
        withCredentials: true,
      });

      toast({
        error: false,
        message: "Thank you for contacting us. We will get back to you soon.",
      });

      setState(INITIAL_VALUE);
    } catch (error: unknown) {
      const errors = getYupErrorMessages(error);

      if (errors) {
        setErrors(errors);
        return;
      }

      toast({
        error: true,
        message: getMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Image
        src="/images/contact-us-banner.webp"
        alt="Contact Us"
        width={960}
        height={200}
        className="w-full"
      />

      <section className="flex justify-center items-center py-16">
        <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-md">
          <h1 className="text-3xl font-bold text-center mb-6">Contact</h1>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Name"
                variant="outlined"
                value={state.name}
                onChange={(e) => {
                  setState((prev) => ({ ...prev, name: e.target.value }));
                }}
                error={!!errors.name}
                helperText={errors.name}
              />
              <TextField
                label="Email"
                variant="outlined"
                value={state.email}
                onChange={(e) => {
                  setState((prev) => ({ ...prev, email: e.target.value }));
                }}
                error={!!errors.email}
                helperText={errors.email}
              />
            </div>

            <TextField
              label="Phone"
              variant="outlined"
              value={state.phone}
              onChange={(e) => {
                setState((prev) => ({ ...prev, phone: e.target.value }));
              }}
              error={!!errors.phone}
              helperText={errors.phone}
            />

            <TextField
              label="Message"
              variant="outlined"
              value={state.message}
              onChange={(e) => {
                setState((prev) => ({ ...prev, message: e.target.value }));
              }}
              multiline
              rows={5}
              error={!!errors.message}
              helperText={errors.message}
            />

            <Button
              type="submit"
              variant="contained"
              loading={loading}
              disabled={loading}
              onClick={submitHandler}
            >
              Submit
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
